import { AfterViewInit, Component, Input, OnDestroy } from '@angular/core';
import { Chart } from 'chart.js/auto';
import {
  LoanCategories,
  LoanService,
  LoanValues,
} from 'src/app/services/loan.service';
import { CommonModule, DecimalPipe } from '@angular/common';
import { tap } from 'rxjs';
import { TranslocoDirective } from '@jsverse/transloco';
import { NeonCurrencyPipe } from 'src/app/shared/pipes/neon-currency.pipe';

@Component({
  selector: 'app-doughnut-chart',
  standalone: true,
  imports: [CommonModule, NeonCurrencyPipe, TranslocoDirective],
  templateUrl: './doughnut-chart.component.html',
  styleUrls: ['./doughnut-chart.component.css'],
})
export class DoughnutChartComponent implements AfterViewInit {
  public fixedDuration = 0;
  public variableDuration = 0;
  public chart: any;

  @Input()
  public showEstimatedFees: boolean = false;
  public totalEstimatedFees = 0;

  private renderFeesText: boolean = false;

  public loanValues: LoanValues | undefined;
  public mortgageLoanSelected = false;

  constructor(private _loanService: LoanService) {}

  public get shouldRenderFeesText(): boolean {
    return this.renderFeesText && this.showEstimatedFees && this.mortgageLoanSelected;
  }

  get loanDurationSignificantlyGreater(): number {
    return (
      (this.loanValues?.loanCalculatorValues)!.loanDuration -
      (this.loanValues?.loanCalculatorValues)!.loanFixedDuration
    );
  }

  ngAfterViewInit(): void {
    if (this.showEstimatedFees) {
      this._loanService.calculateEstimatedFees();
    }

    this._loanService.currentLoanValues
      .pipe(
        tap((res) => {
          this.loanValues = res;
          if (!this.chart) {
            this.createChart();
          } else {
            this.setDurations();
            this.chart.data.datasets[0].data = [
              this.fixedDuration,
              this.variableDuration,
            ];
            this.chart.update();
          }
        })
      )
      .subscribe();

    this._loanService.currentEstimatedFees
      .pipe(
        tap((res) => {
          if (res) {
            this.totalEstimatedFees = 0;
            let property: keyof typeof res;
            for (property in res) {
              this.totalEstimatedFees += res[property]?.fee ?? 0;
            }

            this.renderFeesText = this.totalEstimatedFees > 0;
          }
        })
      )
      .subscribe();

    this._loanService.currentSelectedCategory
      .pipe(
        tap((res) => {
          this.mortgageLoanSelected = LoanCategories[0].id === res;
        })
      )
      .subscribe();

   
  }



  private setDurations() {
    this.fixedDuration = this.loanValues!.loanCalculatorValues?.loanFixedDuration ?? 0;
    this.variableDuration = this.loanValues!.loanCalculatorValues?.loanDuration ?? 0;
    if (this.fixedDuration > 0 && this.variableDuration === this.fixedDuration) {
      this.variableDuration = 0;
    }
  }

  private createChart() {
    const ctx = document.getElementById('yearsChart') as HTMLCanvasElement;

    if (
      this.loanValues!.loanCalculatorValues?.loanDuration &&
      this.loanValues!.loanCalculatorValues.loanFixedDuration
    ) {
      this.setDurations();
      if (ctx) {
        this.chart = new Chart(ctx, {
          type: 'doughnut',
          data: {
            datasets: [
              {
                data: [this.fixedDuration, this.variableDuration],
                backgroundColor: ['#059B9C', '#009B67'],
                hoverOffset: 4,
                borderColor: ['#059B9C', '#009B67'],
                borderRadius: 4,
                spacing: 5,
                offset: 0,
              },
            ],
          },
          options: {
            cutout: '85%',
            responsive: true
          },
        });
      }
    }
  }

  private resizeCanvas = () => {
    const canvas = document.getElementById('yearsChart') as HTMLCanvasElement;
    if (canvas) {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.width = parent.clientWidth;
        canvas.height = parent.clientHeight;
      }
      // Re-render the chart after resizing
      this.chart?.resize();
    }
  };
}
